import { ChangeDetectionStrategy, Component, Injector, OnInit } from "@angular/core";
import { Meeting } from "@common/ADAPT.Common.Model/organisation/meeting";
import { MeetingAgendaTemplate } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-template";
import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";
import { lastValueFrom } from "rxjs";
import { StakeholderServicesService } from "../stakeholder-services.service";

@Component({
    selector: "adapt-copy-meeting-or-template-page",
    templateUrl: "./copy-meeting-or-template-page.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class CopyMeetingOrTemplatePageComponent extends BaseRoutedComponent implements OnInit {
    public sourceOrganisation?: Organisation;
    public sourceTeam?: Team;

    public sourceMeetingOrTemplate?: MeetingAgendaTemplate | Meeting;

    public destinationOrganisation?: Organisation;
    public destinationTeam?: Team;

    public copyAsMeeting = false;

    public isMyMathsOk = false;
    public error?: string;

    constructor(
        private stakeholderServices: StakeholderServicesService,
        private commonDialogService: AdaptCommonDialogService,
        injector: Injector,
    ) {
        super(injector);
    }

    public ngOnInit() {
        this.notifyActivated();
    }

    public get isComplete() {
        if (!this.sourceOrganisation || !this.sourceMeetingOrTemplate || !this.destinationOrganisation) {
            return false;
        }

        if (this.copyAsMeeting && !this.destinationTeam) {
            return false;
        }

        return true;
    }

    public updateMeetingOrTemplate(meetingOrTemplate?: MeetingAgendaTemplate | Meeting) {
        // do it next digest cycle as this callback is from select box selection, which will cause ExpressionChanged...Error.
        setTimeout(() => {
            this.sourceMeetingOrTemplate = meetingOrTemplate;
            if (!meetingOrTemplate) {
                // if template is cleared - the org selection will be removed - so when template is selected again, the select box won't be
                // reflecting the already selected destination, which may end up with inconsistent team/org from the visual component.
                // - clearing these would be the simplest.
                this.destinationOrganisation = undefined;
                this.destinationTeam = undefined;
            }
        });
    }

    @Autobind
    public filterSourceOrganisation(organisation: Organisation) {
        return organisation.organisationId !== this.sourceOrganisation?.organisationId;
    }

    @Autobind
    public filterDestinationTeam(team: Team) {
        return !team.isInactive();
    }

    @Autobind
    public async promiseToCopyMeetingOrTemplate() {
        this.error = "";

        try {
            await lastValueFrom(this.stakeholderServices.copyMeetingOrTemplate({
                MeetingId: (this.sourceMeetingOrTemplate as Meeting)?.meetingId,
                MeetingAgendaTemplateId: (this.sourceMeetingOrTemplate as MeetingAgendaTemplate)?.meetingAgendaTemplateId,
                DestinationOrganisationId: this.destinationOrganisation!.organisationId,
                DestinationTeamId: this.destinationTeam?.teamId,
                DestinationTypeIsMeeting: this.copyAsMeeting,
            }));

            await lastValueFrom(this.commonDialogService.showMessageDialog("Copy Successful", `${this.sourceMeetingOrTemplate?.name} has been successfully been copied to ${this.destinationOrganisation?.name}`));
        } catch (e) {
            this.error = ErrorHandlingUtilities.getHttpResponseMessage(e);
        }
    }

    @Autobind
    public onSourceOrganisationChanged(organisation: Organisation) {
        if (this.sourceOrganisation === organisation) {
            return;
        }

        this.sourceOrganisation = organisation;
        this.sourceMeetingOrTemplate = undefined;
        this.destinationOrganisation = undefined;
        this.destinationTeam = undefined;
    }
}
